import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Box,
  TextInput,
  Button,
  Textarea,
  Flex,
  Text,
  ActionIcon,
  UnstyledButton,
  Badge,
  Modal,
  Divider,
  Grid,
  Pagination,
  Loader,
  Group,
  Select,
  SegmentedControl,
  Switch,
  LoadingOverlay,
} from "@mantine/core";
import axios from "axios";
import toast from "react-hot-toast";
import {
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import {
  IconX,
  IconPencil,
  IconThumbUp,
  IconThumbDown,
  IconTrash,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import {
  BulkAssociate,
  ManagementLayout,
  EmptyMessage,
} from "@components/shared";
import { LocationSelect, BulkLocationDownload } from "./";
import entityColor from "@util/entityColor";
import entityIcon from "@util/entityIcon";

export default function LocationGroup({ organizationId }) {
  const [refresh, setRefresh] = useState(false);
  return (
    <div>
      <ManagementLayout
        dataComponent={
          <GroupList organizationId={organizationId} refresh={refresh} />
        }
        createComponent={
          <Card>
            <Text fw={600} mb="sm">
              Create Group
            </Text>
            <GroupCreate
              createReqData={{
                organization_id: organizationId,
              }}
              organizationId={organizationId}
              onSuccess={() => setRefresh(!refresh)}
            />
          </Card>
        }
      />
    </div>
  );
}

const GroupCreate = ({ onSuccess, createReqData, organizationId }) => {
  const [createdGroup, setCreatedGroup] = useState(null);

  if (createdGroup) {
    return (
      <div>
        <Box mb="xs">
          <Text size="md">
            <b>{createdGroup.name}</b>
          </Text>
          {createdGroup.description && (
            <Text size="sm">{createdGroup.description}</Text>
          )}
        </Box>
        <LocationAdd
          organizationId={organizationId}
          groupId={createdGroup.id}
          onSuccess={() => {
            setCreatedGroup(null);
            onSuccess();
          }}
        />
        <Flex justify="center" mt="md">
          <Button
            size="xs"
            variant="subtle"
            color="gray"
            onClick={() => {
              setCreatedGroup(null);
              onSuccess();
            }}
          >
            Done for now
          </Button>
        </Flex>
      </div>
    );
  }

  return (
    <div>
      <GroupForm
        onSuccess={(gid) => setCreatedGroup(gid)}
        createReqData={createReqData}
      />
    </div>
  );
};

const GroupForm = ({
  createReqData,
  onSuccess,
  groupId,
  name = "",
  description = "",
  status = "1",
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState({
    name: "",
    description: "",
    status: "1",
  });

  useEffect(() => {
    setFormValues({
      name,
      description,
      status: `${status}`,
    });
  }, [name, description, status]);

  function onSubmit() {
    setLoading(true);

    if (groupId) return onUpdate();

    const req = {
      ...formValues,
      ...createReqData,
    };

    axios
      .post("/location-groups/", req)
      .then(({ data }) => {
        setLoading(false);
        onSuccess({
          ...formValues,
          id: data.response[0].id,
        });
      })
      .catch((err) => {
        toast.error(err);
        setLoading(false);
        onSuccess({
          ...formValues,
          id: 13,
        });
      });
  }

  function onUpdate() {
    setLoading(true);

    const req = {
      ...formValues,
      location_group_id: groupId,
    };

    axios
      .put(`/location-groups/${groupId}/`, req)
      .then(({ data }) => {
        setLoading(false);
        onSuccess();
      })
      .catch((err) => {
        toast.error(err);
        setLoading(false);
      });
  }

  return (
    <div>
      <TextInput
        label="Group Name"
        value={formValues.name}
        onChange={(e) => setFormValues({ ...formValues, name: e.target.value })}
        mb="xs"
      />
      <Textarea
        label="Description (optional)"
        value={formValues.description}
        onChange={(e) =>
          setFormValues({ ...formValues, description: e.target.value })
        }
        mb="xs"
      />
      {groupId && (
        <Switch
          label={t("common:active")}
          checked={formValues.status === "1"}
          onChange={() => {
            setFormValues({
              ...formValues,
              status: formValues.status === "1" ? "2" : "1",
            });
          }}
          size="xs"
          mb="lg"
          mt="lg"
        />
      )}
      <Button
        fullWidth
        mt="md"
        onClick={onSubmit}
        loading={loading}
        disabled={!formValues.name}
      >
        {groupId ? t("common:update") : t("common:create")}
      </Button>
    </div>
  );
};

const LocationAdd = ({
  organizationId,
  groupId,
  onSuccess,
  existingLocations = [],
}) => {
  const [bulkAdd, setBulkAdd] = useState(false);
  const [locations, setLocations] = useState([]);
  const locationIds = [
    ...locations.map((l) => l.id),
    ...existingLocations.map((l) => l.id),
  ];
  const [loading, setLoading] = useState(false);
  // const [selectedLocationId, setSelectedLocationId] = useState("");

  function onBulkSuccess() {
    onSuccess();
  }

  function onManualSubmit() {
    setLoading(true);

    const req = {
      location_group_id: groupId,
      location_ids: locations.map((l) => l.id),
      // location_id: selectedLocationId,
      organization_id: organizationId,
    };

    axios
      .post("/location-groups/add-location/", req)
      .then(() => {
        setLoading(false);
        onSuccess();
        setLocations([]);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  return (
    <div>
      <UnstyledButton onClick={() => setBulkAdd(!bulkAdd)} mb="sm">
        <Text c="blue" size="xs" fw={600}>
          {bulkAdd
            ? "Back to selecting locations individually"
            : "Adding a lot of locations? Upload a CSV"}
        </Text>
      </UnstyledButton>
      {bulkAdd ? (
        <Box mt="xs">
          <BulkAssociate
            uploadReqData={{
              location_group_id: groupId,
            }}
            uploadUrl={`/location-groups/bulk-add-locations/`}
            onSuccess={onBulkSuccess}
            buttonProps={{
              size: "sm",
              fullWidth: true,
            }}
          />
        </Box>
      ) : (
        <>
          <LocationSelect
            disabledIds={locationIds}
            onChange={(e) => setLocations([...locations, e])}
            reqData={{
              organization_id: organizationId,
              location_group_id: groupId,
            }}
            objectOnChange
            // onChange={(e) => setSelectedLocationId(e)}
            label="Location Search"
            // value={selectedLocationId}
          />
          {locations.length > 0 && (
            <Box mt="xs">
              <LocationList
                locations={locations}
                onLocationRemove={(id) =>
                  setLocations([...locations].filter((l) => l.id !== id))
                }
              />
              <Text size="xs" mt="xs" fw={600}>
                {locations.length} location(s) queued
              </Text>
            </Box>
          )}
          <Button
            fullWidth
            mt="md"
            onClick={onManualSubmit}
            loading={loading}
            disabled={locations.length === 0}
          >
            Add locations to group
          </Button>
        </>
      )}
      {bulkAdd && (
        <>
          <Divider my="lg" />
          <Flex justify="center">
            <BulkLocationDownload
              buttonText="Download Organization Locations CSV"
              downloadReqData={{
                organization_id: organizationId,
                context: "org_add_locations",
              }}
            />
          </Flex>
        </>
      )}
    </div>
  );
};

const LocationList = ({ locations, onLocationRemove }) => {
  const sortedLocations = locations.sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  return (
    <div>
      <Box
        style={{
          minHeight: 100,
          maxHeight: 300,
          overflowY: "auto",
          border: "1px solid var(--mantine-color-gray-3)",
        }}
      >
        {sortedLocations.map((location, i) => (
          <Box
            p="xs"
            key={location.id}
            style={{
              background: i % 2 === 0 ? "var(--mantine-color-gray-0)" : "white",
              borderRadius: 4,
            }}
          >
            <Flex justify="space-between">
              <Text style={{ flexGrow: 1 }} size="xs">
                {location.name}
              </Text>
              <ActionIcon
                onClick={() => onLocationRemove(location)}
                size="xs"
                color="red"
                radius="xl"
                variant="light"
              >
                <IconX size={12} />
              </ActionIcon>
            </Flex>
          </Box>
        ))}
      </Box>
    </div>
  );
};

const GroupList = ({ organizationId, refresh }) => {
  const { t } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(-1);
  const [total, setTotal] = useState(0);
  const [init, setInit] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search_value: "",
    status: "active",
  });
  const CancelToken = axios.CancelToken;
  const cancelRef = useRef(null);

  const table = useReactTable({
    data: groups,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  useEffect(() => {
    resetTablePage().then(() => {
      fetchData();
    });
  }, [JSON.stringify(filters)]);

  useEffect(() => {
    if (!init) return;
    fetchData();
  }, [JSON.stringify(table.getState().pagination)]);

  useEffect(() => {
    fetchData();
  }, [refresh]);

  async function resetTablePage() {
    try {
      table.setPageIndex(0);
    } catch (error) {}
  }

  function fetchData() {
    setError(null);

    const req = {
      ...filters,
      organization_id: organizationId,
      page_size: table.getState().pagination.pageSize || 10,
      page:
        table.getState().pagination.pageIndex === -1
          ? 0
          : table.getState().pagination.pageIndex,
    };

    if ([null, undefined].includes(req.page) || !req.page) {
      req.page = 0;
    }

    const cancel = cancelRef.current;
    if (cancel) cancel();

    setLoading(true);

    axios
      .post("/retrieve-location-groups/", req, {
        cancelToken: new CancelToken(function executor(c) {
          cancelRef.current = c;
        }),
      })
      .then(({ data }) => {
        setGroups(data.response[0].data);
        setPageCount(data.response[0].page_count);
        if (data.response[0].total !== undefined) {
          setTotal(data.response[0].total);
        } else {
          setTotal(data.response[0].data.length);
        }
      })
      .then(() => {
        setInit(true);
        setLoading(false);
        window.scrollTo(0, 0);
      })
      .catch((err) => {
        setLoading(false);
        setInit(true);
      });
  }

  return (
    <div>
      <TextInput
        placeholder={t("common:search")}
        size="lg"
        value={filters.search_value}
        onChange={(e) =>
          setFilters({ ...filters, search_value: e.target.value })
        }
        mb="sm"
      />
      <Box mb="md">
        <SegmentedControl
          size="xs"
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e })}
          data={[
            { label: t("common:active"), value: "active" },
            { label: t("common:inactive"), value: "inactive" },
          ]}
        />
      </Box>
      {!loading && total === 0 && !error && <EmptyMessage />}
      {table.getRowModel().rows.map(({ original: group }) => (
        <GroupItem
          key={group.id}
          group={group}
          fetchData={fetchData}
          organizationId={organizationId}
        />
      ))}
      <Group mt="lg">
        <Pagination
          total={pageCount}
          onChange={(e) => table.setPageIndex(e - 1)}
          size="lg"
          disabled={loading}
          value={table.getState().pagination.pageIndex + 1}
        />
        <Select
          size="sm"
          style={{ width: "80px" }}
          value={`${table.getState().pagination.pageSize}`}
          onChange={(e) => table.setPageSize(parseInt(e))}
          data={[5, 10, 20, 30, 40, 50].map((pageSize) => ({
            label: `${pageSize}`,
            value: `${pageSize}`,
          }))}
        />
        {loading && <Loader size="xs" variant="dots" />}
      </Group>
      <Text mb="lg" size="sm" mt="sm">
        {total} {t("common:total")}{" "}
        {total === 1 ? t("common:item_singular") : t("common:item_plural")}
      </Text>
    </div>
  );
};

const GroupItem = ({ group, fetchData, organizationId }) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  return (
    <Card mb="xs">
      <Flex gap="xs" align="center">
        <div style={{ flexGrow: 1 }}>
          <Flex align="center" gap="xs">
            <Text fw={700}>{group.name}</Text>
          </Flex>
          <Text size="sm">{group.description}</Text>
        </div>
        <div>
          <Flex gap="xs">
            <ActionIcon
              size="md"
              variant="light"
              color="gray"
              radius="xl"
              onClick={() => setIsEditing(!isEditing)}
              title="Edit Group"
            >
              <IconPencil size={16} />
            </ActionIcon>
            <GroupLocations
              group={group}
              organizationId={organizationId}
              fetchData={fetchData}
            />
          </Flex>
        </div>
      </Flex>
      <Modal
        title={`${t("common:edit")} ${group.name}`}
        opened={isEditing}
        onClose={() => setIsEditing(false)}
      >
        <GroupForm
          groupId={group.id}
          name={group.name}
          description={group.description}
          status={group.status}
          onSuccess={() => {
            setIsEditing(false);
            fetchData();
          }}
        />
        <Divider my="lg" />
        <Box>
          <DeleteGroup
            groupId={group.id}
            onSuccess={() => {
              setIsEditing(false);
              fetchData();
            }}
          />
        </Box>
      </Modal>
    </Card>
  );
};

const DeleteGroup = ({ groupId, onSuccess }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  function onSubmit() {
    setLoading(true);

    axios
      .delete(`/location-groups/${groupId}/`)
      .then(() => {
        setLoading(false);
        onSuccess();
      })
      .catch((err) => {
        toast.error(err);
        setLoading(false);
      });
  }

  if (confirmDelete) {
    return (
      <div>
        <Text size="sm" mb="xs" fw={600} ta="center">
          Are you sure you want to delete this group?
        </Text>
        <div>
          <Flex justify="center" gap="xs">
            <Button
              variant="light"
              color="red"
              onClick={onSubmit}
              size="xs"
              loading={loading}
            >
              {t("common:delete")}
            </Button>
            <Button
              variant="light"
              onClick={() => setConfirmDelete(false)}
              size="xs"
            >
              {t("common:cancel")}
            </Button>
          </Flex>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Flex justify="center" gap="xs">
        <Button
          variant="subtle"
          color="gray"
          size="xs"
          onClick={() => setConfirmDelete(true)}
        >
          Delete Group
        </Button>
      </Flex>
    </div>
  );
};

const GroupLocations = ({ group, organizationId, fetchData }) => {
  const [editingLocations, setEditingLocations] = useState(false);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search_value: "",
  });

  useEffect(() => {
    if (!editingLocations) return;
    fetchLocations();
  }, [editingLocations]);

  function onClose() {
    if (loading) return;
    setEditingLocations(false);
    setLoading(false);
    fetchData();
  }

  function fetchLocations() {
    setLoading(true);
    axios
      .get(`/location-groups/${group.id}/locations/`)
      .then(({ data }) => {
        setLocations(data.response);
      })
      .catch((err) => {
        setLocations([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function onLocationRemove(location) {
    // if no location, remove all locations

    setLoading(true);
    axios
      .post(`/location-groups/remove-location/`, {
        location_group_location_ids: location
          ? [location.id]
          : locations.map((l) => l.id),
      })
      .then(() => {
        fetchLocations();
      })
      .catch((err) => {
        toast.error(err);
      });
  }

  const filteredLocations = locations.filter((l) =>
    l.name.toLowerCase().includes(filters.search_value.toLowerCase())
  );

  const hasLocations = locations.length > 0;

  return (
    <div>
      <Button
        size="xs"
        leftSection={entityIcon.location(16)}
        variant="light"
        color={entityColor.location}
        radius="xl"
        onClick={() => setEditingLocations(!editingLocations)}
        title="Edit Locations"
      >
        {group.location_count}
      </Button>
      <Modal
        opened={editingLocations}
        onClose={onClose}
        title={`Edit ${group.name} locations`}
        size="70%"
      >
        <div style={{ position: "relative" }}>
          <LoadingOverlay visible={loading} />
          <Grid gutter="xl">
            <Grid.Col span={{ base: 12, md: 6 }}>
              {hasLocations && (
                <Flex align="center" gap="xs" mb="sm">
                  <Text fw={600} size="sm">
                    {locations.length} location(s) in this group
                  </Text>
                  <RemoveAllLocations onClick={() => onLocationRemove()} />
                </Flex>
              )}
              {!hasLocations && (
                <Flex
                  justify="center"
                  mt="md"
                  align="center"
                  direction="column"
                  gap="xs"
                  p="xl"
                  h={300}
                  style={{
                    background: "var(--mantine-color-gray-0)",
                    padding: "4px 8px",
                    borderRadius: 4,
                    border: "1px solid var(--mantine-color-gray-3)",
                  }}
                >
                  <div>{entityIcon.location(48)}</div>
                  <Text size="md" ta="center">
                    No locations in this group. Add some to get started.
                  </Text>
                </Flex>
              )}
              {hasLocations && (
                <div>
                  <TextInput
                    placeholder="Search for a location..."
                    size="sm"
                    mb="xs"
                    value={filters.search_value}
                    onChange={(e) =>
                      setFilters({ ...filters, search_value: e.target.value })
                    }
                  />
                  <LocationList
                    locations={filteredLocations}
                    onLocationRemove={onLocationRemove}
                  />
                  <Flex justify="center" mt="md">
                    <BulkLocationDownload
                      buttonText={`Download ${group.name} Locations CSV`}
                      downloadReqData={{
                        context: "location_group",
                        location_group_id: group.id,
                      }}
                    />
                  </Flex>
                </div>
              )}
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Text fw={600} size="sm" mb="xs">
                Add Locations
              </Text>
              <LocationAdd
                organizationId={organizationId}
                groupId={group.id}
                onSuccess={() => {
                  fetchLocations();
                }}
                existingLocations={locations.map((m) => ({
                  id: m.location_id,
                  name: m.name,
                }))}
              />
            </Grid.Col>
          </Grid>
        </div>
      </Modal>
    </div>
  );
};

const RemoveAllLocations = ({ onClick }) => {
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (confirmDelete) {
    return (
      <Flex
        align="center"
        gap="xs"
        style={{
          background: "var(--mantine-color-gray-0)",
          padding: "4px 8px",
          borderRadius: 4,
          border: "1px solid var(--mantine-color-gray-3)",
        }}
      >
        <Text size="xs" fw={600}>
          Remove all locations?
        </Text>
        <ActionIcon
          radius="xl"
          color="green"
          variant="light"
          onClick={onClick}
          size="sm"
        >
          <IconThumbUp size={14} />
        </ActionIcon>
        <ActionIcon
          variant="light"
          onClick={() => setConfirmDelete(false)}
          size="sm"
          radius="xl"
          color="red"
        >
          <IconThumbDown size={14} />
        </ActionIcon>
      </Flex>
    );
  }

  return (
    <ActionIcon
      variant="light"
      color="gray"
      size="sm"
      radius="xl"
      onClick={() => setConfirmDelete(true)}
      title="Remove all locations"
    >
      <IconTrash size={14} />
    </ActionIcon>
  );
};
